/**
 * Format cents to dollar string: 1999 → "$19.99"
 */
export function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Format cents to exact dollar string with cents: 1999 → "$19.99"
 */
export function formatCurrencyExact(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/**
 * Format mileage with commas: 123456 → "123,456"
 */
export function formatMileage(miles: number | null | undefined): string {
  if (miles == null) return '—';
  return new Intl.NumberFormat('en-US').format(miles);
}

/**
 * Format phone to (xxx) xxx-xxxx from E.164
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

/**
 * Parse phone input to E.164: "(555) 123-4567" → "+15551234567"
 */
export function toE164(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
  return `+${digits}`;
}

/**
 * Format date to short display: "2026-02-21T..." → "Feb 21, 2026"
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format relative time: "2 hours ago", "3 days ago"
 */
export function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/**
 * Generate vehicle display title
 */
export function vehicleTitle(v: {
  year?: number | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
}): string {
  const parts = [v.year, v.make, v.model, v.trim].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Unknown Vehicle';
}

/**
 * Slugify text for URLs: "Joe's Auto Sales" → "joes-auto-sales"
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
